import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  Image,
  ShoppingBag,
  Upload,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Lock,
  Mail,
  MessageCircle,
  X,
} from "lucide-react";

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Admin Login Component
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/admin-login", { email, password });
      if (response.data.role === "admin") {
        localStorage.setItem("admin_session", response.data.session_token);
        onLogin(response.data);
        toast.success("Bienvenido al panel de administración");
      } else {
        setError("No tenés permisos de administrador");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl text-white">ACCESO STAFF</h1>
          <p className="text-white/60 text-sm">Barraca Maldonado</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm mb-2">
            <p className="font-medium">Acceso solo para personal autorizado</p>
            <p className="text-xs mt-1">Si sos cliente, ingresá desde "Iniciar Sesión" en el sitio.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="admin-email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@barracamaldonado.com"
              required
              data-testid="admin-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-password" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Contraseña
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="admin-password"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Ingresando..." : "Ingresar"}
          </Button>

          <div className="text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
              Volver al sitio
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// Admin Layout
function AdminLayout({ children, adminUser, onLogout }) {
  const location = useLocation();

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/users", icon: Users, label: "Usuarios" },
    { path: "/admin/products", icon: Package, label: "Productos" },
    { path: "/admin/slides", icon: Image, label: "Slides" },
    { path: "/admin/orders", icon: ShoppingBag, label: "Pedidos" },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-primary text-white min-h-screen sticky top-0 hidden md:block">
          <div className="p-6">
            <h2 className="font-heading text-xl">ADMIN</h2>
            <p className="text-white/60 text-sm">Barraca Maldonado</p>
            {adminUser && (
              <p className="text-white/40 text-xs mt-1 truncate">{adminUser.email}</p>
            )}
          </div>
          <nav className="px-4 space-y-1">
            {navItems.map(({ path, icon: Icon, label, exact }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                  isActive(path, exact)
                    ? "bg-white/20 text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-4 left-4 right-4 space-y-2">
            <button 
              onClick={onLogout}
              className="w-full text-white/60 hover:text-white text-sm flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded transition-colors"
            >
              <Lock className="w-4 h-4" />
              Cerrar sesión admin
            </button>
            <Link to="/" className="text-white/60 hover:text-white text-sm flex items-center gap-2 px-4">
              <ChevronLeft className="w-4 h-4" />
              Volver al sitio
            </Link>
          </div>
        </aside>

        {/* Mobile nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-primary text-white z-40">
          <nav className="flex justify-around py-2">
            {navItems.map(({ path, icon: Icon, label, exact }) => (
              <Link
                key={path}
                to={path}
                className={`flex flex-col items-center p-2 ${
                  isActive(path, exact) ? "text-white" : "text-white/60"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs mt-1">{label}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Main content */}
        <main className="flex-1 p-6 md:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>
    </div>
  );
}

// Dashboard
function Dashboard() {
  const [stats, setStats] = useState({ products: 0, orders: 0, slides: 0 });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, ordersRes, slidesRes] = await Promise.all([
        api.get("/products?limit=1"),
        api.get("/admin/orders?limit=1"),
        api.get("/slides?active_only=false"),
      ]);
      setStats({
        products: productsRes.data.total,
        orders: ordersRes.data.total,
        slides: slidesRes.data.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl mb-6">DASHBOARD</h1>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-sm border p-6">
          <Package className="w-8 h-8 text-primary mb-2" />
          <p className="text-3xl font-heading">{stats.products}</p>
          <p className="text-muted-foreground">Productos</p>
        </div>
        <div className="bg-white rounded-sm border p-6">
          <ShoppingBag className="w-8 h-8 text-accent mb-2" />
          <p className="text-3xl font-heading">{stats.orders}</p>
          <p className="text-muted-foreground">Pedidos</p>
        </div>
        <div className="bg-white rounded-sm border p-6">
          <Image className="w-8 h-8 text-primary mb-2" />
          <p className="text-3xl font-heading">{stats.slides}</p>
          <p className="text-muted-foreground">Slides</p>
        </div>
      </div>
    </div>
  );
}

// Products Admin
function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/products?page=${page}&limit=10`);
      setProducts(response.data.products);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/admin/upload-products", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult({
        success: true,
        added: response.data.added,
        updated: response.data.updated,
      });
      toast.success(`Productos cargados: ${response.data.added} nuevos, ${response.data.updated} actualizados`);
      fetchProducts();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al cargar archivo";
      setUploadResult({
        success: false,
        error: errorMsg,
      });
      toast.error(errorMsg);
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm("¿Estás seguro de eliminar este producto?")) return;
    
    try {
      await api.delete(`/products/${productId}`);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch (error) {
      toast.error("Error al eliminar producto");
    }
  };

  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || "Sin categoría";

  // Generate and download Excel template
  const downloadTemplate = () => {
    // Create CSV content with headers and example data
    const headers = ["name", "price", "category_id", "description", "sku", "stock", "brand", "images", "is_featured"];
    const exampleRows = [
      ["Cemento Portland 50kg", "12.50", categories[0]?.category_id || "cat_ejemplo123", "Cemento de alta calidad para construcción", "CEM-001", "100", "Ancap", "https://ejemplo.com/imagen1.jpg,https://ejemplo.com/imagen2.jpg", "true"],
      ["Taladro Percutor 700W", "89.00", categories[1]?.category_id || "cat_ejemplo456", "Taladro profesional con función percutor", "TAL-001", "25", "Stanley", "https://ejemplo.com/taladro.jpg", "false"],
      ["Pintura Látex Interior 20L", "85.00", categories[4]?.category_id || "cat_ejemplo789", "Pintura látex lavable para interiores", "PIN-001", "50", "Inca", "", "true"],
    ];
    
    const csvContent = [
      headers.join(","),
      ...exampleRows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "plantilla_productos_barraca.csv";
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Plantilla descargada");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-heading text-2xl md:text-3xl">PRODUCTOS</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Importar Excel
          </Button>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">IMPORTAR PRODUCTOS DESDE EXCEL</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Step 1: Download template */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                <div className="flex-1">
                  <h4 className="font-medium mb-1">Descargá la plantilla</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Usá nuestra plantilla con el formato correcto. Incluye ejemplos de productos.
                  </p>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Descargar Plantilla CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2: Fill data */}
            <div className="bg-muted/50 border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-muted-foreground/20 text-muted-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                <div className="flex-1">
                  <h4 className="font-medium mb-1">Completá tus productos</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Abrí el archivo en Excel o Google Sheets y agregá tus productos.
                  </p>
                  
                  {/* Column reference */}
                  <div className="bg-white rounded border p-3 text-xs space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="font-semibold text-accent">Columnas requeridas:</span>
                        <ul className="mt-1 space-y-0.5 text-muted-foreground">
                          <li><code className="bg-muted px-1 rounded">name</code> - Nombre del producto</li>
                          <li><code className="bg-muted px-1 rounded">price</code> - Precio (número)</li>
                          <li><code className="bg-muted px-1 rounded">category_id</code> - ID de categoría</li>
                        </ul>
                      </div>
                      <div>
                        <span className="font-semibold">Columnas opcionales:</span>
                        <ul className="mt-1 space-y-0.5 text-muted-foreground">
                          <li><code className="bg-muted px-1 rounded">description</code> - Descripción</li>
                          <li><code className="bg-muted px-1 rounded">sku</code> - Código único</li>
                          <li><code className="bg-muted px-1 rounded">stock</code> - Cantidad</li>
                          <li><code className="bg-muted px-1 rounded">brand</code> - Marca</li>
                          <li><code className="bg-muted px-1 rounded">images</code> - URLs separadas por coma</li>
                          <li><code className="bg-muted px-1 rounded">is_featured</code> - true/false</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Category IDs reference */}
                  {categories.length > 0 && (
                    <div className="mt-3 bg-white rounded border p-3">
                      <p className="text-xs font-semibold mb-2">IDs de Categorías disponibles:</p>
                      <div className="flex flex-wrap gap-1">
                        {categories.map(cat => (
                          <span key={cat.category_id} className="inline-flex items-center gap-1 bg-muted text-xs px-2 py-1 rounded">
                            <span className="font-medium">{cat.name}:</span>
                            <code className="text-primary">{cat.category_id}</code>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step 3: Upload */}
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                <div className="flex-1">
                  <h4 className="font-medium mb-1">Subí tu archivo</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Seleccioná el archivo Excel (.xlsx, .xls) o CSV (.csv) con tus productos.
                  </p>
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploading ? 'bg-muted border-muted-foreground/30' : 'border-accent/50 hover:border-accent hover:bg-accent/5'}`}>
                      {uploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm font-medium">Procesando archivo...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-accent" />
                          <span className="text-sm font-medium">Click para seleccionar archivo</span>
                          <span className="text-xs text-muted-foreground">o arrastrá y soltá aquí</span>
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Upload result */}
                  {uploadResult && (
                    <div className={`mt-4 p-3 rounded-lg ${uploadResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                      {uploadResult.success ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">
                            {uploadResult.added} productos nuevos, {uploadResult.updated} actualizados
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">{uploadResult.error}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="text-xs text-muted-foreground bg-muted/30 rounded p-3">
              <p className="font-medium mb-1">Tips:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Si el SKU ya existe, el producto se actualizará en lugar de duplicarse</li>
                <li>Los precios deben ser números sin símbolo de moneda (ej: 12.50)</li>
                <li>Para múltiples imágenes, separá las URLs con coma</li>
                <li>Guardá el archivo como CSV o Excel (.xlsx) antes de subirlo</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Producto</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Precio</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead className="w-24">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No hay productos. <button onClick={() => setShowUploadDialog(true)} className="text-accent hover:underline">Importá desde Excel</button>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.product_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-muted rounded overflow-hidden">
                        {product.images?.[0] && (
                          <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium line-clamp-1">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.sku || "Sin SKU"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getCategoryName(product.category_id)}</TableCell>
                  <TableCell>USD ${product.price}</TableCell>
                  <TableCell>
                    <Badge variant={product.stock > 0 ? "default" : "destructive"}>
                      {product.stock}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(product.product_id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="icon"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// Slides Admin
function SlidesAdmin() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    image_url: "",
    cta_text: "",
    cta_link: "",
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const response = await api.get("/slides?active_only=false");
      setSlides(response.data);
    } catch (error) {
      console.error("Error fetching slides:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlide) {
        await api.put(`/slides/${editingSlide.slide_id}`, formData);
        toast.success("Slide actualizado");
      } else {
        await api.post("/slides", formData);
        toast.success("Slide creado");
      }
      setEditingSlide(null);
      setFormData({ title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", order: 0, is_active: true });
      fetchSlides();
    } catch (error) {
      toast.error("Error al guardar slide");
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title,
      subtitle: slide.subtitle || "",
      image_url: slide.image_url,
      cta_text: slide.cta_text || "",
      cta_link: slide.cta_link || "",
      order: slide.order,
      is_active: slide.is_active,
    });
  };

  const handleDelete = async (slideId) => {
    if (!confirm("¿Estás seguro de eliminar este slide?")) return;
    
    try {
      await api.delete(`/slides/${slideId}`);
      toast.success("Slide eliminado");
      fetchSlides();
    } catch (error) {
      toast.error("Error al eliminar slide");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl">SLIDES</h1>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Slide
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSlide ? "Editar Slide" : "Nuevo Slide"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Título *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                />
              </div>
              <div>
                <Label>URL de Imagen *</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texto del Botón</Label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Link del Botón</Label>
                  <Input
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select
                    value={formData.is_active ? "active" : "inactive"}
                    onValueChange={(v) => setFormData({ ...formData, is_active: v === "active" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90">
                {editingSlide ? "Actualizar" : "Crear"} Slide
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <p>Cargando...</p>
        ) : slides.length === 0 ? (
          <p className="text-muted-foreground">No hay slides</p>
        ) : (
          slides.map((slide) => (
            <div key={slide.slide_id} className="bg-white rounded-sm border overflow-hidden">
              <div className="aspect-video bg-muted relative">
                <img
                  src={slide.image_url}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <Badge
                  className="absolute top-2 right-2"
                  variant={slide.is_active ? "default" : "secondary"}
                >
                  {slide.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              <div className="p-4">
                <h3 className="font-heading">{slide.title}</h3>
                {slide.subtitle && <p className="text-sm text-muted-foreground">{slide.subtitle}</p>}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(slide)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(slide.slide_id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Orders Admin
function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [whatsappModal, setWhatsappModal] = useState({ open: false, link: null, orderInfo: "" });

  useEffect(() => {
    fetchOrders();
  }, [page, filterType, filterStatus]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let params = `?page=${page}&limit=10`;
      if (filterType !== "all") params += `&order_type=${filterType}`;
      if (filterStatus !== "all") params += `&status=${filterStatus}`;
      
      const response = await api.get(`/admin/orders${params}`);
      setOrders(response.data.orders);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await api.put(`/admin/orders/${orderId}/status`, { 
        status: newStatus,
        note: statusNote || null,
        send_notification: true
      });
      toast.success("Estado actualizado");
      setStatusNote("");
      fetchOrders();
      setSelectedOrder(null);
      
      // Show WhatsApp modal if link is available
      if (response.data.notifications?.whatsapp_link) {
        const statusLabels = {
          pending: "Pendiente",
          quoted: "Cotizado",
          confirmed: "Confirmado",
          processing: "En Preparación",
          shipped: "Enviado",
          delivered: "Entregado",
          cancelled: "Cancelado"
        };
        setWhatsappModal({
          open: true,
          link: response.data.notifications.whatsapp_link,
          orderInfo: `#${orderId.slice(-8)} → ${statusLabels[newStatus] || newStatus}`
        });
      }
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-UY", { style: "currency", currency: "USD" }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-UY", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Pendiente", class: "bg-yellow-100 text-yellow-800" },
      quoted: { label: "Cotizado", class: "bg-blue-100 text-blue-800" },
      confirmed: { label: "Confirmado", class: "bg-green-100 text-green-800" },
      processing: { label: "En Preparación", class: "bg-purple-100 text-purple-800" },
      shipped: { label: "Enviado", class: "bg-indigo-100 text-indigo-800" },
      delivered: { label: "Entregado", class: "bg-green-100 text-green-800" },
      cancelled: { label: "Cancelado", class: "bg-red-100 text-red-800" },
    };
    const c = config[status] || config.pending;
    return <Badge className={`${c.class} hover:${c.class}`}>{c.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === "quote") {
      return <Badge variant="outline" className="border-primary text-primary">Cotización</Badge>;
    }
    return <Badge variant="outline" className="border-accent text-accent">Pedido</Badge>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-heading text-2xl md:text-3xl">PEDIDOS Y COTIZACIONES</h1>
        <div className="flex gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="order">Pedidos</SelectItem>
              <SelectItem value="quote">Cotizaciones</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="quoted">Cotizados</SelectItem>
              <SelectItem value="confirmed">Confirmados</SelectItem>
              <SelectItem value="processing">En Preparación</SelectItem>
              <SelectItem value="shipped">Enviados</SelectItem>
              <SelectItem value="delivered">Entregados</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pedido</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No hay pedidos
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.order_id} className={order.status === "pending" ? "bg-yellow-50" : ""}>
                  <TableCell>
                    <p className="font-medium">#{order.order_id.slice(-8)}</p>
                    <p className="text-xs text-muted-foreground">
                      {order.items?.length || 0} producto{order.items?.length !== 1 ? "s" : ""}
                    </p>
                  </TableCell>
                  <TableCell>
                    {getTypeBadge(order.order_type)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{order.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{order.customer_phone}</p>
                    <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                  </TableCell>
                  <TableCell className="font-heading text-accent">
                    {formatPrice(order.total)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(order.created_at)}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(order.status)}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedOrder(order)}
                    >
                      Ver / Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="icon"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-heading">
                    {selectedOrder.order_type === "quote" ? "COTIZACIÓN" : "PEDIDO"} #{selectedOrder.order_id.slice(-8)}
                  </span>
                  {getStatusBadge(selectedOrder.status)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Customer Info */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Cliente</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Nombre:</span>
                      <span className="ml-2 font-medium">{selectedOrder.customer_name}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Teléfono:</span>
                      <span className="ml-2">{selectedOrder.customer_phone}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <span className="ml-2">{selectedOrder.customer_email}</span>
                    </div>
                    {selectedOrder.customer_city && (
                      <div>
                        <span className="text-muted-foreground">Ciudad:</span>
                        <span className="ml-2">{selectedOrder.customer_city}</span>
                      </div>
                    )}
                    {selectedOrder.customer_address && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Dirección:</span>
                        <span className="ml-2">{selectedOrder.customer_address}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Products */}
                <div>
                  <h4 className="font-medium mb-2">Productos</h4>
                  <div className="border rounded-lg divide-y">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3">
                        {item.image && (
                          <img src={item.image} alt="" className="w-12 h-12 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatPrice(item.price)} x {item.quantity}
                          </p>
                        </div>
                        <p className="font-heading">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    ))}
                    <div className="p-3 bg-muted/50 flex justify-between">
                      <span className="font-heading">TOTAL</span>
                      <span className="font-heading text-xl text-accent">{formatPrice(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedOrder.notes && (
                  <div>
                    <h4 className="font-medium mb-1">Notas del cliente</h4>
                    <p className="text-sm bg-muted/50 p-3 rounded">{selectedOrder.notes}</p>
                  </div>
                )}

                {/* Status History */}
                {selectedOrder.status_history?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Historial</h4>
                    <div className="space-y-2">
                      {selectedOrder.status_history.map((h, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-primary mt-1.5"></div>
                          <div>
                            <p>
                              <span className="font-medium capitalize">{h.status}</span>
                              <span className="text-muted-foreground ml-2">
                                {formatDate(h.timestamp)}
                              </span>
                            </p>
                            {h.note && <p className="text-muted-foreground">{h.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Change Status */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Cambiar Estado</h4>
                  <div className="flex flex-wrap gap-2">
                    {["pending", "quoted", "confirmed", "processing", "shipped", "delivered", "cancelled"].map((status) => (
                      <Button
                        key={status}
                        variant={selectedOrder.status === status ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleStatusChange(selectedOrder.order_id, status)}
                        disabled={selectedOrder.status === status}
                      >
                        {status === "pending" && "Pendiente"}
                        {status === "quoted" && "Cotizado"}
                        {status === "confirmed" && "Confirmado"}
                        {status === "processing" && "En Preparación"}
                        {status === "shipped" && "Enviado"}
                        {status === "delivered" && "Entregado"}
                        {status === "cancelled" && "Cancelado"}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* WhatsApp Notification Modal for Orders */}
      <Dialog open={whatsappModal.open} onOpenChange={(open) => setWhatsappModal({ ...whatsappModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="w-6 h-6 text-green-500" />
              Notificar por WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Se envió un email automático sobre el cambio de estado del pedido.
            </p>
            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="font-mono font-medium">{whatsappModal.orderInfo}</p>
            </div>
            <p className="text-sm">
              ¿Querés también notificar al cliente por WhatsApp? Hacé click para abrir la conversación con el mensaje pre-armado.
            </p>
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => {
                  window.open(whatsappModal.link, '_blank');
                  setWhatsappModal({ open: false, link: null, orderInfo: "" });
                }}
              >
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                Abrir WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => setWhatsappModal({ open: false, link: null, orderInfo: "" })}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Users Admin
function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [whatsappModal, setWhatsappModal] = useState({ open: false, link: null, userName: "", action: "" });

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await api.get(`/admin/users${params}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (userId, status) => {
    try {
      const user = users.find(u => u.user_id === userId);
      const response = await api.put(`/admin/users/${userId}/status`, { 
        status,
        send_notification: true
      });
      
      toast.success(status === "approved" ? "Usuario aprobado" : "Usuario rechazado");
      fetchUsers();
      
      // Show WhatsApp modal if link is available
      if (response.data.notifications?.whatsapp_link) {
        setWhatsappModal({
          open: true,
          link: response.data.notifications.whatsapp_link,
          userName: user?.name || "Usuario",
          action: status === "approved" ? "aprobación" : "rechazo"
        });
      }
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (error) {
      toast.error("Error al eliminar usuario");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Aprobado</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Rechazado</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pendiente</Badge>;
    }
  };

  const pendingCount = users.filter(u => u.status === "pending").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">USUARIOS</h1>
          {pendingCount > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              <span className="text-yellow-600 font-medium">{pendingCount}</span> usuarios pendientes de aprobación
            </p>
          )}
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="approved">Aprobados</SelectItem>
            <SelectItem value="rejected">Rechazados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Contacto</TableHead>
              <TableHead>RUT/CI</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="w-32">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No hay usuarios
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.user_id} className={user.status === "pending" ? "bg-yellow-50" : ""}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{user.phone}</p>
                      {user.city && <p className="text-muted-foreground">{user.city}</p>}
                      {user.address && <p className="text-muted-foreground text-xs">{user.address}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{user.rut || "-"}</span>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(user.status)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString("es-UY")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {user.status === "pending" && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateStatus(user.user_id, "approved")}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Aprobar"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => updateStatus(user.user_id, "rejected")}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Rechazar"
                          >
                            <XCircle className="w-5 h-5" />
                          </Button>
                        </>
                      )}
                      {user.status !== "pending" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => updateStatus(user.user_id, "pending")}
                          className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          title="Volver a pendiente"
                        >
                          <Clock className="w-5 h-5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteUser(user.user_id)}
                        className="text-destructive hover:text-destructive"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* WhatsApp Notification Modal */}
      <Dialog open={whatsappModal.open} onOpenChange={(open) => setWhatsappModal({ ...whatsappModal, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="w-6 h-6 text-green-500" />
              Notificar por WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Se envió un email automático a <strong>{whatsappModal.userName}</strong> sobre la {whatsappModal.action} de su cuenta.
            </p>
            <p className="text-sm">
              ¿Querés también notificarlo por WhatsApp? Hacé click en el botón para abrir la conversación con el mensaje pre-armado.
            </p>
            <div className="flex gap-2">
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => {
                  window.open(whatsappModal.link, '_blank');
                  setWhatsappModal({ open: false, link: null, userName: "", action: "" });
                }}
              >
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                Abrir WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => setWhatsappModal({ open: false, link: null, userName: "", action: "" })}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Main Admin component with authentication
export default function Admin() {
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkAdminSession();
  }, []);

  const checkAdminSession = async () => {
    const token = localStorage.getItem("admin_session");
    if (!token) {
      setChecking(false);
      return;
    }

    try {
      const response = await api.get("/auth/admin-verify", {
        headers: { "X-Admin-Token": token }
      });
      if (response.data.role === "admin") {
        setAdminUser(response.data);
      }
    } catch (error) {
      localStorage.removeItem("admin_session");
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_session");
    setAdminUser(null);
    toast.success("Sesión cerrada");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return <AdminLogin onLogin={setAdminUser} />;
  }

  return (
    <AdminLayout adminUser={adminUser} onLogout={handleLogout}>
      <Routes>
        <Route path="/admin" element={<Dashboard />} />
        <Route path="/admin/users" element={<UsersAdmin />} />
        <Route path="/admin/products" element={<ProductsAdmin />} />
        <Route path="/admin/slides" element={<SlidesAdmin />} />
        <Route path="/admin/orders" element={<OrdersAdmin />} />
      </Routes>
    </AdminLayout>
  );
}